import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: string | number, currency: string = 'USD'): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function shortId(uuid: string): string {
  return uuid.slice(0, 8)
}

/**
 * Map org currency → Intl locale for date formatting.
 * DD/MM/YYYY locales use day-first; MM/DD/YYYY locales use month-first.
 */
const CURRENCY_LOCALE: Record<string, string> = {
  USD: 'en-US',   // MM/DD/YYYY
  CAD: 'en-CA',   // YYYY-MM-DD (Canadian standard)
  GBP: 'en-GB',   // DD/MM/YYYY
  AUD: 'en-AU',   // DD/MM/YYYY
  NZD: 'en-NZ',   // DD/MM/YYYY
  EUR: 'de-DE',   // DD.MM.YYYY
  JPY: 'ja-JP',   // YYYY/MM/DD
  CHF: 'de-CH',   // DD.MM.YYYY
  SGD: 'en-SG',   // DD/MM/YYYY
  HKD: 'en-HK',   // DD/MM/YYYY
}

/** Format an ISO date string as a short date (no time) using the org's currency locale. */
export function fmtDate(iso: string, currency?: string): string {
  const locale = (currency && CURRENCY_LOCALE[currency]) || 'en-NZ'
  return new Date(iso).toLocaleDateString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

/** Filter an input value to only allow numeric characters (0-9 . ,) and
 *  enforce a maximum number of decimal places (default 2).
 *  When the decimal portion is fully entered and all zeros (e.g. 5.00),
 *  it collapses to the whole number (5). */
export function numericOnly(value: string, maxDp: number = 2): string {
  const negative = value.startsWith('-')
  const stripped = value.replace(/[^0-9.,]/g, '')
  const dotIdx = stripped.indexOf('.')
  const base = (() => {
    if (dotIdx === -1) return stripped
    if (maxDp === 0) return stripped.slice(0, dotIdx)
    const before = stripped.slice(0, dotIdx)
    const after = stripped.slice(dotIdx + 1).replace(/\./g, '').slice(0, maxDp)
    if (after.length === maxDp && /^0+$/.test(after)) return before
    return `${before}.${after}`
  })()
  // Allow lone minus (user is mid-typing a negative number)
  return negative && base !== '' ? `-${base}` : (negative && base === '' ? '-' : base)
}

/** Strip trailing decimal zeros from a stored value: "0.0" → "0", "5.00" → "5".
 *  Unlike numericOnly, safe for complete (non-typing) values. */
export function cleanZeros(v: string): string {
  if (!v.includes('.')) return v
  const [int, dec] = v.split('.')
  const trimmed = dec.replace(/0+$/, '')
  return `${int}.${trimmed.padEnd(2, '0')}`
}
